import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AreaChart, Area, ResponsiveContainer, Tooltip } from "recharts";

/**
 * Compact per-drop sparkline: sales-per-day for the last N days.
 * Lightweight — 32px tall, no axes, only a subtle area + hover tooltip.
 */
export default function ProductSparkline({ productId, days = 30 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.get(`/creator/products/${productId}/analytics`, { params: { days } })
      .then(({ data }) => { if (!cancel) setData(data); })
      .catch(() => { if (!cancel) setData(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [productId, days]);

  if (loading) return <div className="h-8" />;
  if (!data || data.total_sales === 0) {
    return (
      <div className="h-8 flex items-center font-mono text-[9px] uppercase tracking-[0.2em] text-white/30">
        // no sales in {days}d
      </div>
    );
  }

  return (
    <div data-testid={`sparkline-${productId}`} className="h-8 -mx-1">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data.series} margin={{ top: 2, right: 2, bottom: 0, left: 2 }}>
          <defs>
            <linearGradient id={`grad-${productId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#CCFF00" stopOpacity={0.7} />
              <stop offset="100%" stopColor="#CCFF00" stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="sales"
            stroke="#CCFF00"
            strokeWidth={1.5}
            fill={`url(#grad-${productId})`}
            isAnimationActive={false}
            dot={false}
          />
          <Tooltip
            cursor={false}
            wrapperStyle={{ outline: "none" }}
            contentStyle={{
              background: "#050505",
              border: "1px solid rgba(255,255,255,0.15)",
              padding: "6px 8px",
              fontFamily: "JetBrains Mono, ui-monospace, monospace",
              fontSize: "10px",
              color: "#fafafa",
            }}
            labelFormatter={(l) => l}
            formatter={(v, n, p) => [`${p.payload.sales} sales · $${p.payload.revenue.toFixed(2)}`, ""]}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
